import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Users,
  MessageCircle,
  Sparkles,
  Heart,
  Shield,
} from "lucide-react";
import SiteNavbar from "../components/SiteNavbar";
import SiteFooter from "../components/SiteFooter";
import CircleCard from "../components/CircleCard";
import {
  CIRCLES_STATS,
  CIRCLES_PILLARS,
  FEATURED_CIRCLES,
  CIRCLE_TYPES,
  CIRCLES_HOW_IT_WORKS,
  CIRCLES_FAQ,
  CIRCLES_TESTIMONIALS,
} from "../data/circlesContent";

const ease = [0.21, 0.47, 0.32, 0.98];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.75, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const PILLAR_ICONS = [Users, MessageCircle, Sparkles];

const CirclesPage = () => {
  const [activeType, setActiveType] = useState("All circles");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredCircles =
    activeType === "All circles"
      ? FEATURED_CIRCLES
      : FEATURED_CIRCLES.filter(
          (c) => c.tag === activeType || c.tags?.includes(activeType),
        );

  return (
    <div
      data-testid="circles-page"
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-32 md:pt-40 pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease }}
            className="text-center max-w-3xl mx-auto"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              A warm community
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
              style={{ color: "var(--text)" }}
            >
              Reading is better{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                together
              </span>
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: "var(--text-soft)" }}
            >
              Reader Circles are small groups on PagePeers — weekly highlights,
              spoiler-safe threads, and recommendations from people who have
              actually read the book, not an algorithm guessing from your clicks.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.2 }}
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          >
            {CIRCLES_STATS.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl p-6 text-center cozy-card"
                style={{ background: "var(--surface)" }}
              >
                <p
                  className="font-serif text-3xl md:text-4xl font-medium mb-1"
                  style={{ color: "var(--brand)" }}
                >
                  {stat.value}
                </p>
                <p
                  className="text-xs uppercase tracking-wider"
                  style={{ color: "var(--text-muted)" }}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>

          <div className="mt-20 md:mt-28 grid md:grid-cols-3 gap-6">
            {CIRCLES_PILLARS.map((pillar, i) => {
              const Icon = PILLAR_ICONS[i] || Users;
              return (
                <FadeIn key={pillar.title} delay={i * 0.08}>
                  <div
                    className="rounded-3xl p-8 h-full"
                    style={{
                      background: "var(--bg-alt)",
                      border: "1px solid var(--border-soft)",
                    }}
                  >
                    <Icon
                      className="w-6 h-6 mb-5"
                      style={{ color: "var(--brand)" }}
                    />
                    <h2
                      className="font-serif text-xl font-medium mb-3"
                      style={{ color: "var(--text)" }}
                    >
                      {pillar.title}
                    </h2>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--text-soft)" }}
                    >
                      {pillar.desc}
                    </p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>

        {/* Featured circles */}
        <section className="mt-24 md:mt-32">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn>
              <h2
                className="font-serif text-3xl md:text-4xl font-medium mb-3"
                style={{ color: "var(--text)" }}
              >
                Circles open now
              </h2>
              <p
                className="text-base max-w-xl mb-10"
                style={{ color: "var(--text-soft)" }}
              >
                Six communities to explore — each tied to a curated shelf you can
                browse before you join.
              </p>
            </FadeIn>

            <div className="flex flex-wrap gap-2 mb-10">
              {CIRCLE_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setActiveType(type)}
                  className="text-xs md:text-sm px-4 py-2 rounded-full border transition-all"
                  style={{
                    borderColor:
                      activeType === type ? "var(--brand)" : "var(--border)",
                    background:
                      activeType === type ? "var(--brand-tint)" : "var(--surface)",
                    color: activeType === type ? "var(--brand)" : "var(--text-soft)",
                    fontWeight: activeType === type ? 600 : 400,
                  }}
                >
                  {type}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCircles.map((circle, i) => (
                <FadeIn key={circle.id} delay={i * 0.05}>
                  <CircleCard
                    circle={circle}
                    testId={`circles-page-card-${i}`}
                  />
                </FadeIn>
              ))}
            </div>

            {filteredCircles.length === 0 && (
              <p
                className="text-center py-12 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                No circles match this filter — try another type.
              </p>
            )}
          </div>
        </section>

        {/* Culture */}
        <section
          className="mt-24 md:mt-32 py-20 md:py-28"
          style={{ background: "var(--bg-alt)" }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <FadeIn>
                <div className="flex items-center gap-3 mb-6">
                  <Shield
                    className="w-6 h-6"
                    style={{ color: "var(--brand)" }}
                  />
                  <span
                    className="text-[11px] uppercase tracking-[0.2em] font-semibold"
                    style={{ color: "var(--brand)" }}
                  >
                    Circle culture
                  </span>
                </div>
                <h2
                  className="font-serif text-3xl md:text-4xl font-medium mb-6"
                  style={{ color: "var(--text)" }}
                >
                  Warm conversations, not loud threads
                </h2>
                <p
                  className="text-base leading-[1.85] mb-5"
                  style={{ color: "var(--text-soft)" }}
                >
                  PagePeers circles are moderated lightly and led by members who
                  care about the books. We prioritize spoiler tags, generous
                  listening, and posts that invite reply — not hot takes for
                  engagement.
                </p>
                <p
                  className="text-base leading-[1.85]"
                  style={{ color: "var(--text-soft)" }}
                >
                  Hosts can pin reading schedules, link a related shelf, and set
                  norms in a welcome note. Members can mute threads, lurk, or
                  leave without drama.
                </p>
              </FadeIn>
              <FadeIn delay={0.1}>
                <div className="space-y-4">
                  {CIRCLES_TESTIMONIALS.map((t) => (
                    <blockquote
                      key={t.author}
                      className="rounded-2xl p-6"
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      <Heart
                        className="w-4 h-4 mb-3"
                        style={{ color: "var(--brand)" }}
                      />
                      <p
                        className="text-sm leading-relaxed italic mb-4"
                        style={{ color: "var(--text)" }}
                      >
                        “{t.quote}”
                      </p>
                      <footer className="text-xs" style={{ color: "var(--text-muted)" }}>
                        — {t.author}, {t.role}
                      </footer>
                    </blockquote>
                  ))}
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* How it works */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <FadeIn>
            <h2
              className="font-serif text-3xl md:text-4xl font-medium text-center mb-4"
              style={{ color: "var(--text)" }}
            >
              How circles work
            </h2>
            <p
              className="text-center max-w-xl mx-auto mb-16 text-base"
              style={{ color: "var(--text-soft)" }}
            >
              From browsing to your first highlight in three steps.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 md:gap-10">
            {CIRCLES_HOW_IT_WORKS.map((item, i) => (
              <FadeIn key={item.step} delay={i * 0.1}>
                <div className="text-center md:text-left">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto md:mx-0 mb-5 cozy-card"
                    style={{ background: "var(--surface)" }}
                  >
                    <span
                      className="font-serif text-2xl italic font-medium"
                      style={{ color: "var(--brand)" }}
                    >
                      {item.step}
                    </span>
                  </div>
                  <h3
                    className="font-serif text-xl font-medium mb-3"
                    style={{ color: "var(--text)" }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--text-soft)" }}
                  >
                    {item.body}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* Host a circle */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <FadeIn>
            <div
              className="rounded-3xl p-10 md:p-14 grid lg:grid-cols-2 gap-10 items-center"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                boxShadow: "var(--card-shadow)",
              }}
            >
              <div>
                <Users
                  className="w-8 h-8 mb-6"
                  style={{ color: "var(--brand)" }}
                />
                <h2
                  className="font-serif text-3xl md:text-4xl font-medium mb-4"
                  style={{ color: "var(--text)" }}
                >
                  Start your own circle
                </h2>
                <p
                  className="text-base leading-relaxed mb-6"
                  style={{ color: "var(--text-soft)" }}
                >
                  Host a book club for friends, a genre room for strangers, or a
                  slow read with twelve kindred spirits. You set the tone — we
                  provide the quiet tools.
                </p>
                <ul className="space-y-2 text-sm" style={{ color: "var(--text-soft)" }}>
                  <li>· Custom welcome note and reading schedule</li>
                  <li>· Link a curated shelf as the circle library</li>
                  <li>· Open, invite-only, or hidden visibility</li>
                  <li>· Optional weekly digest for members</li>
                </ul>
              </div>
              <div
                className="rounded-2xl p-8 md:p-10"
                style={{ background: "var(--bg-alt)" }}
              >
                <p
                  className="font-serif text-2xl italic leading-snug mb-6"
                  style={{ color: "var(--text)" }}
                >
                  “We are only nine people, but it feels like the best table at
                  the bookstore.”
                </p>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  — Host, Slow Canon Club
                </p>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <FadeIn>
            <h2
              className="font-serif text-3xl md:text-4xl font-medium text-center mb-12"
              style={{ color: "var(--text)" }}
            >
              Circle questions
            </h2>
          </FadeIn>
          <div className="space-y-4">
            {CIRCLES_FAQ.map((item, i) => (
              <FadeIn key={item.q} delay={i * 0.06}>
                <div
                  className="rounded-2xl p-6 md:p-8 cozy-card"
                  style={{ background: "var(--surface)" }}
                >
                  <h3
                    className="font-serif text-lg md:text-xl font-medium mb-3"
                    style={{ color: "var(--text)" }}
                  >
                    {item.q}
                  </h3>
                  <p
                    className="text-sm md:text-base leading-relaxed"
                    style={{ color: "var(--text-soft)" }}
                  >
                    {item.a}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="rounded-3xl p-10 md:p-14 text-center"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
              border: "1px solid var(--border)",
            }}
          >
            <h2
              className="font-serif text-3xl md:text-4xl font-medium mb-4"
              style={{ color: "var(--text)" }}
            >
              Find your circle today
            </h2>
            <p
              className="text-base mb-8 max-w-lg mx-auto"
              style={{ color: "var(--text-soft)" }}
            >
              Join a room that reads like you do — or start one and invite the
              friends who always ask “what are you reading?”
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://club.pagepeers.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-transform hover:scale-[1.02]"
                style={{
                  background: "var(--brand)",
                  color: "var(--on-brand)",
                  boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
                }}
              >
                Join the Club
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/shelf/reader-circles"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text)",
                  background: "var(--surface)",
                }}
              >
                Reader Circles shelf
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default CirclesPage;
